import type { DropResult } from '@hello-pangea/dnd';
import is from '@sindresorhus/is';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useFieldArray, useWatch } from 'react-hook-form';

import type { Competency } from '~/shared/data-contract/job-profile';
import type { JobProfileSkill } from '~/shared/data-contract/skill';

import type { CompetencyWithSkills, ProfileForm } from '../types';

/** How a skill differs from the other side of a comparison; `changed` means only its proficiency level differs. */
export type SkillDiff = 'added' | 'changed' | 'removed';
export type JobProfileSkillWithDiff = JobProfileSkill & { readonly diff?: SkillDiff };
export const WITHOUT_COMPETENCY_ID = 'without-competency';
export const LANGUAGE_SKILLS_ID = 'language-skills';
const SYSTEM_CATEGORY_IDS = new Set([WITHOUT_COMPETENCY_ID, LANGUAGE_SKILLS_ID]);

export const isSystemCategory = (competencyId: string): boolean => SYSTEM_CATEGORY_IDS.has(competencyId);

const canMoveCompetency = (
    competencies: readonly CompetencyWithSkills[],
    sourceIndex: number,
    targetIndex: number,
): boolean => {
    const sourceCompetency = competencies[sourceIndex];
    const targetCompetency = competencies[targetIndex];

    return (
        !is.undefined(sourceCompetency) &&
        !is.undefined(targetCompetency) &&
        !SYSTEM_CATEGORY_IDS.has(sourceCompetency.id) &&
        !SYSTEM_CATEGORY_IDS.has(targetCompetency.id)
    );
};

// Merges current competencies with any new competencies introduced by skills.
// Keeps all existing competencies and appends only those referenced by skills
// that don't already exist, to avoid duplicates.
export const mergeCompetenciesFromSkills = (
    currentCompetencies: readonly CompetencyWithSkills[],
    skills: readonly JobProfileSkill[],
    allCompetencies: ReadonlyMap<string, Competency> | undefined,
): readonly Competency[] => {
    // Keep all real competencies and exclude synthetic UI categories.
    const existing = currentCompetencies.filter((c) => !SYSTEM_CATEGORY_IDS.has(c.id));
    const existingIds = new Set(existing.map((c) => c.id));

    // Find competencies referenced by skills that aren't already present
    const newFromSkills = skills
        .filter((skill) => is.nonEmptyString(skill.competencyId) && !existingIds.has(skill.competencyId))
        .map((skill) => allCompetencies?.get(skill.competencyId!))
        .filter((competency) => !is.undefined(competency));

    // Deduplicate — multiple skills can reference the same new competency
    const uniqueNew = [...new Map(newFromSkills.map((c) => [c.id, c])).values()];

    return [...existing, ...uniqueNew];
};

export const addSkillsToCompetencies = (
    competencies: readonly Competency[] = [],
    skills: readonly JobProfileSkillWithDiff[] = [],
): CompetencyWithSkills[] => {
    const nonLanguageSkills = skills.filter((skill) => skill.skillType !== 'language');
    const languageSkills = skills.filter((skill) => skill.skillType === 'language');
    const competenciesWithSkills = [...competencies]
        .map((competency: Competency) => {
            return {
                ...competency,
                skills: nonLanguageSkills.filter((skill) => skill.competencyId === competency.id),
            };
        })
        .filter((competency) => !SYSTEM_CATEGORY_IDS.has(competency.id));

    const withoutCompetency = {
        id: WITHOUT_COMPETENCY_ID,
        skills: nonLanguageSkills.filter((skill) => is.undefined(skill.competencyId)),
        title: '',
        titles: [],
    };

    const languageSkillsGroup: CompetencyWithSkills[] =
        languageSkills.length > 0
            ? [
                  {
                      id: LANGUAGE_SKILLS_ID,
                      skills: languageSkills,
                      title: 'Language',
                      titles: [],
                  },
              ]
            : [];

    // Skills without a competency stay at the top; only language skills sink to the bottom.
    return [withoutCompetency, ...competenciesWithSkills, ...languageSkillsGroup];
};

const setSkillProficiency = (
    formMethods: UseFormReturn<ProfileForm>,
    skillId: string,
    level: JobProfileSkill['level'],
): void => {
    formMethods.setValue(
        'skills',
        formMethods.getValues('skills').map((skill) => ({
            ...skill,
            // biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration
            level: skill.id === skillId ? (level as any) : skill.level,
        })),
    );
};

const setCompetency = (item: JobProfileSkillWithDiff, competencyTarget: CompetencyWithSkills): void => {
    if (item.skillType === 'language' || competencyTarget.id === LANGUAGE_SKILLS_ID) {
        item.competencyId = undefined;
        return;
    }

    item.competencyId = competencyTarget.id === WITHOUT_COMPETENCY_ID ? undefined : competencyTarget.id;
};

export const useSkillsWithCompetencies = ({
    formMethods,
    onManualEdit,
}: {
    readonly formMethods: UseFormReturn<ProfileForm>;
    readonly onManualEdit: () => void;
}): {
    readonly changeProficiencyLevel: (skillId: string, level: JobProfileSkill['level']) => void;
    readonly competenciesWithSkills: CompetencyWithSkills[];
    readonly move: (instructions: DropResult<string>) => void;
    readonly remove: (index: number) => void;
    readonly removeCompetency: (competencyId: string) => void;
    readonly removeSkill: (competencyId: string, skillId: string) => void;
} => {
    const { move: moveCompetency, remove } = useFieldArray({
        control: formMethods.control,
        name: 'competencies',
        rules: {
            validate: (value) => {
                const allSkillsHaveLevel = value.every((competency) =>
                    competency.skills.every((skill) => typeof skill.level === 'number'),
                );

                return allSkillsHaveLevel || 'All skills must have a proficiency level';
            },
        },
    });

    const skills = useWatch({
        control: formMethods.control,
        name: 'skills',
    });

    const competenciesWithSkills = useWatch({
        control: formMethods.control,
        name: 'competencies',
    });

    useEffect(() => {
        const current = formMethods.getValues('competencies');
        const next = addSkillsToCompetencies(current, skills);
        if (!isEqual(current, next)) {
            formMethods.setValue('competencies', next);
        }
    }, [formMethods, skills]);

    const changeProficiencyLevel = useCallback(
        (skillId: string, level: JobProfileSkill['level']): void => {
            onManualEdit();
            setSkillProficiency(formMethods, skillId, level);
        },
        [formMethods, onManualEdit],
    );

    const removeSkill = useCallback(
        (competencyId: string, skillId: string): void => {
            const competency = competenciesWithSkills.find((competency) => competency.id === competencyId);
            if (is.undefined(competency)) {
                return;
            }

            onManualEdit();
            const nextCompetenciesWithSkills = competenciesWithSkills.map((competency) =>
                competency.id === competencyId
                    ? {
                          ...competency,
                          skills: competency.skills.filter((skill) => skill.id !== skillId),
                      }
                    : competency,
            );

            formMethods.setValue('competencies', nextCompetenciesWithSkills);
            formMethods.setValue(
                'skills',
                nextCompetenciesWithSkills.flatMap((competency) => competency.skills),
            );
        },
        [competenciesWithSkills, formMethods, onManualEdit],
    );

    const removeCompetency = useCallback(
        (competencyId: string): void => {
            const index = competenciesWithSkills.findIndex((competency) => competency.id === competencyId);
            if (index === -1) {
                return;
            }

            onManualEdit();
            remove(index);
        },
        [competenciesWithSkills, onManualEdit, remove],
    );

    const move = useCallback(
        (instructions: DropResult<string>): void => {
            const { destination: target, source, type } = instructions;

            if (is.nullOrUndefined(target)) {
                return;
            }

            if (type === 'competency') {
                if (!canMoveCompetency(competenciesWithSkills, source.index, target.index)) {
                    return;
                }

                onManualEdit();
                moveCompetency(source.index, target.index);
                return;
            }

            if (type !== 'skill') {
                return;
            }

            const competencySource = competenciesWithSkills.find((competency) => competency.id === source.droppableId);

            const competencyTarget = competenciesWithSkills.find((competency) => competency.id === target.droppableId);

            if (
                is.undefined(competencySource) ||
                is.undefined(competencyTarget) ||
                competencyTarget.id === LANGUAGE_SKILLS_ID
            ) {
                return;
            }

            const item = competencySource.skills[source.index];

            if (is.undefined(item)) {
                return;
            }

            onManualEdit();
            setCompetency(item, competencyTarget);

            competencySource.skills.splice(source.index, 1);
            competencyTarget.skills.splice(target.index, 0, item);

            const skills = competenciesWithSkills.flatMap((competency) => competency.skills);

            formMethods.setValue('skills', skills);

            formMethods.setValue('competencies', competenciesWithSkills);
        },
        [competenciesWithSkills, formMethods, moveCompetency, onManualEdit],
    );

    return { changeProficiencyLevel, competenciesWithSkills, move, remove, removeCompetency, removeSkill };
};
